import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { LuCircleCheck, LuCircleX, LuInfo, LuX } from 'react-icons/lu'
import './BankNotifications.css'

// URL base del backend API
const getBackendBaseUrl = () => {
  const configured = import.meta.env.VITE_API_URL || ''
  if (configured) return configured.replace(/\/api\/?$/, '')
  if (window.location.hostname === 'app.flashnow.lat') return 'https://api.flashnow.lat'
  return 'http://127.0.0.1:4000'
}

function BankNotifications() {
  const { userRole, isAuthenticated, isOperator, assignedAccounts } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [connected, setConnected] = useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notifications_enabled')
    return saved === null ? true : saved === 'true'
  })
  const eventSourceRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const isConnectingRef = useRef(false)

  // Escuchar cambios en la preferencia de notificaciones
  useEffect(() => {
    const handleToggle = (event) => {
      setNotificationsEnabled(event.detail)
    }
    window.addEventListener('notificationsToggle', handleToggle)
    return () => window.removeEventListener('notificationsToggle', handleToggle)
  }, [])

  // Obtener CVUs del usuario para filtrar notificaciones
  const getUserCvus = () => {
    if (isOperator) return [] // El operador recibe todo, no necesita filtrar
    if (!assignedAccounts || !Array.isArray(assignedAccounts)) return []
    return assignedAccounts.map(acc => acc.cvu).filter(cvu => cvu)
  }

  useEffect(() => {
    // Solo conectar para usuarios CREDENCIAL autenticados Y con notificaciones habilitadas
    // Nota: isOperator (urbana) siempre recibe notificaciones, no puede desactivarlas
    const shouldConnect = isAuthenticated && userRole === 'CREDENCIAL' && (isOperator || notificationsEnabled) && localStorage.getItem('flash_market') !== 'CL'
    
    if (!shouldConnect) {
      // Cerrar conexión si existe
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
        setConnected(false)
        console.log('📡 SSE desconectado (notificaciones deshabilitadas)')
      }
      return
    }

    // Si ya hay una conexión activa, no reconectar
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      console.log('📡 SSE ya conectado, ignorando reconexión')
      return
    }

    // Evitar múltiples conexiones simultáneas
    if (isConnectingRef.current) {
      console.log('📡 SSE ya conectándose, ignorando')
      return
    }

    const connectSSE = () => {
      // Evitar reconexiones mientras ya se está conectando
      if (isConnectingRef.current) return
      isConnectingRef.current = true

      // Construir URL con parámetros de filtrado
      const userCvus = getUserCvus()
      const params = new URLSearchParams()
      params.set('isOperator', isOperator ? 'true' : 'false')
      if (userCvus.length > 0) {
        params.set('cvus', userCvus.join(','))
      }
      
      const sseUrl = `${getBackendBaseUrl()}/api/webhooks/notifications/stream?${params.toString()}`
      console.log('📡 Conectando a SSE:', sseUrl)
      console.log('   isOperator:', isOperator)
      console.log('   CVUs:', userCvus.length)
      
      const eventSource = new EventSource(sseUrl)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        console.log('✅ SSE conexión abierta')
        isConnectingRef.current = false
      }

      eventSource.onerror = () => {
        setConnected(false)
        isConnectingRef.current = false
        eventSource.close()
        eventSourceRef.current = null
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = setTimeout(() => {
          const shouldReconnect = isAuthenticated && userRole === 'CREDENCIAL' && (isOperator || notificationsEnabled) && localStorage.getItem('flash_market') !== 'CL'
          if (shouldReconnect) connectSSE()
        }, 30000)
      }

      // Evento de conexión exitosa
      eventSource.addEventListener('connected', (event) => {
        console.log('✅ SSE conectado:', event.data)
        setConnected(true)
        isConnectingRef.current = false
      })

      // Notificaciones bancarias
      eventSource.addEventListener('bank-notification', (event) => {
        try {
          const notification = JSON.parse(event.data)
          console.log('📥 Notificación recibida:', notification)
          
          const notifId = notification.id || Date.now()
          
          setNotifications(prev => [
            { ...notification, id: notifId, visible: true },
            ...prev.slice(0, 9)
          ])

          // Auto-ocultar después de 15 segundos
          setTimeout(() => {
            setNotifications(prev => 
              prev.map(n => n.id === notifId ? { ...n, visible: false } : n)
            )
          }, 15000)

        } catch (err) {
          console.error('Error parseando notificación:', err)
        }
      })

      // Heartbeat
      eventSource.addEventListener('heartbeat', () => {
        console.log('💓 SSE heartbeat')
      })
    }

    connectSSE()

    // Cleanup al desmontar
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
        console.log('📡 SSE desconectado (cleanup)')
      }
      isConnectingRef.current = false
    }
  }, [isAuthenticated, userRole, isOperator, notificationsEnabled, assignedAccounts])

  const dismissNotification = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, visible: false } : n)
    )
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0)
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'finished':
        return <LuCircleCheck className="status-icon success" />
      case 'failed':
      case 'error':
        return <LuCircleX className="status-icon error" />
      default:
        return <LuInfo className="status-icon info" />
    }
  }

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'finished':
        return 'notification-success'
      case 'failed':
      case 'error':
        return 'notification-error'
      default:
        return 'notification-info'
    }
  }

  // No renderizar nada si no es CREDENCIAL
  if (userRole !== 'CREDENCIAL') {
    return null
  }

  const visibleNotifications = notifications.filter(n => n.visible)

  return (
    <div className="bank-notifications-container">
      {/* Lista de notificaciones */}
      <div className="notifications-list">
        {visibleNotifications.map((notification) => (
          <div 
            key={notification.id} 
            className={`notification-popup ${getStatusClass(notification.status)}`}
          >
            <button 
              className="notification-close"
              onClick={() => dismissNotification(notification.id)}
            >
              <LuX />
            </button>
            
            <div className="notification-header">
              {getStatusIcon(notification.status)}
              <span className="notification-title">
                {notification.status === 'completed' || notification.status === 'finished' ? 'Transferencia Completada' : 
                 notification.status === 'failed' ? 'Transferencia Fallida' :
                 'Notificación Bancaria'}
              </span>
            </div>

            <div className="notification-body">
              {notification.amount && (
                <div className="notification-amount">
                  {formatCurrency(notification.amount)}
                </div>
              )}
              
              {notification.description && (
                <div className="notification-description">
                  {notification.description}
                </div>
              )}

              {notification.from_cvu && notification.to_cvu && (
                <div className="notification-accounts">
                  <span className="account-label">Origen:</span>
                  <span className="account-value">{notification.from_cvu.substring(0, 10)}...</span>
                  <span className="account-label">Destino:</span>
                  <span className="account-value">{notification.to_cvu.substring(0, 10)}...</span>
                </div>
              )}

              {notification.error_message && (
                <div className="notification-error">
                  {notification.error_message}
                </div>
              )}

              {notification.authorization_code && (
                <div className="notification-auth">
                  Código: {notification.authorization_code}
                </div>
              )}
            </div>

            <div className="notification-footer">
              <span className="notification-time">
                {new Date(notification.timestamp).toLocaleTimeString('es-AR')}
              </span>
              {notification.process_id && (
                <span className="notification-process">
                  Lote: {notification.process_id}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default BankNotifications
